import { createFileRoute, redirect, Outlet, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Users, FileText, Package, Calendar, LogOut, Search,
  Building2, Inbox, ChevronRight, Menu, X, Plus, Phone, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_employee")({
  ssr: false,
  head: () => ({ meta: [{ name: "robots", content: "noindex, nofollow" }] }),
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/medewerker/login" });
    return { user: data.user };
  },
  component: EmployeeLayout,
});

const nav = [
  { to: "/medewerker/office", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/medewerker/office/aanvragen", label: "Aanvragen", icon: Inbox },
  { to: "/medewerker/office/doorgestuurd", label: "Doorgestuurd", icon: Send },
  { to: "/medewerker/office/offertes", label: "Offertes", icon: FileText },
  { to: "/medewerker/office/orders", label: "Orders", icon: Package },
  { to: "/medewerker/office/planning", label: "Planning", icon: Calendar },
  { to: "/medewerker/office/klanten", label: "Klanten", icon: Users },
  { to: "/medewerker/office/telefonie", label: "Telefonie", icon: Phone },
];

function NavLink({ to, label, icon: Icon, exact, onClick }: any) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = exact ? pathname === to : pathname.startsWith(to);
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
        active
          ? "bg-[#0b2a4a] text-white shadow-sm"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <Icon className={`size-4 shrink-0 ${active ? "text-[#7dd3fc]" : "text-slate-400"}`} />
      <span className="flex-1 truncate">{label}</span>
      {active && <ChevronRight className="size-3.5" />}
    </Link>
  );
}

function EmployeeLayout() {
  const { user, profile, roles, isAdmin, signOut, loading } = useAuth();
  const nav2 = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isEmployee = roles.includes("employee") || isAdmin;

  useEffect(() => {
    if (!loading && user && !isEmployee) {
      nav2({ to: "/medewerker/login", search: { error: "no_access" } as any });
    }
  }, [loading, user, isEmployee, nav2]);

  const out = async () => {
    await signOut();
    nav2({ to: "/medewerker/login" });
  };
  const close = () => setMobileOpen(false);

  if (loading || !user) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-400">Laden…</div>;
  }
  if (!isEmployee) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow ring-1 ring-slate-200">
          <h2 className="text-lg font-bold">Geen toegang</h2>
          <p className="mt-2 text-sm text-slate-500">
            Uw account heeft geen medewerkersrol. Vraag een beheerder om toegang.
          </p>
          <Button onClick={out} className="mt-4">Uitloggen</Button>
        </div>
      </div>
    );
  }

  const Sidebar = (
    <div className="flex h-full flex-col bg-white p-4">
      <div className="mb-6 flex items-center justify-between">
        <Link to="/medewerker/office" onClick={close} className="flex items-center gap-2.5">
          <div className="flex size-9 items-center justify-center rounded-xl bg-[#0b2a4a] text-white">
            <Building2 className="size-4" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-tight text-slate-900">EVG Office</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Medewerker</div>
          </div>
        </Link>
        <button onClick={close} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden" aria-label="Sluit">
          <X className="size-5" />
        </button>
      </div>

      <div className="mb-4 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
        <p className="truncate text-sm font-semibold text-slate-900">{profile?.full_name ?? user.email}</p>
        <p className="truncate text-xs text-slate-500">{user.email}</p>
        <div className="mt-2 flex gap-1.5">
          {isAdmin && <span className="rounded-full bg-[#0b2a4a] px-2 py-0.5 text-[10px] font-semibold uppercase text-white">Admin</span>}
          {roles.includes("employee") && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">Medewerker</span>}
        </div>
      </div>

      <nav className="flex-1 space-y-1">
        {nav.map((n) => (
          <NavLink key={n.to} {...n} onClick={close} />
        ))}
      </nav>

      <div className="mt-4 space-y-1">
        {isAdmin && (
          <Link to="/admin" className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100">
            <LayoutDashboard className="size-4" /> Naar admin
          </Link>
        )}
        <button
          onClick={out}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100"
        >
          <LogOut className="size-4" /> Uitloggen
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 border-r border-slate-200 bg-white z-40">
        {Sidebar}
      </aside>

      {/* Mobile bar */}
      <div className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 lg:hidden">
        <Link to="/medewerker/office" className="flex items-center gap-2">
          <div className="flex size-8 items-center justify-center rounded-lg bg-[#0b2a4a] text-white">
            <Building2 className="size-4" />
          </div>
          <span className="text-sm font-bold text-slate-900">EVG Office</span>
        </Link>
        <Button variant="outline" size="sm" onClick={() => setMobileOpen(true)} className="gap-2">
          <Menu className="size-4" /> Menu
        </Button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="absolute inset-y-0 left-0 h-full w-72 max-w-[85vw] bg-white shadow-xl overflow-y-auto">
            {Sidebar}
          </div>
        </div>
      )}

      <main className="min-w-0 lg:ml-64">
        {/* Topbar */}
        <div className="sticky top-0 z-30 hidden lg:flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-6 backdrop-blur">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Zoek offertenr, klant, order…"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#0b2a4a] focus:bg-white"
            />
          </div>
          <Link
            to="/medewerker/office/offertes/nieuw"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0b2a4a] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0e3463]"
          >
            <Plus className="size-3.5" /> Nieuwe offerte
          </Link>
        </div>
        <div className="w-full px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
